import '../App.css';
import React from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import AsyncStorage from '@react-native-async-storage/async-storage';
import getApiBaseUrl from '../utils/apiConfig';
import PasswordPrompt from '../components/PasswordPrompt';

export class StoreRequest extends React.Component {
  constructor(props) {
    super(props);

    // Check URL for 'returning' parameter to skip directly to activation screen
    const urlParams = new URLSearchParams(window.location.search);
    const isReturningUser = urlParams.get('returning') === 'true';

    this.state = {
      // Form fields
      business_name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      business_description: '',
      business_category: '',
      average_transaction_size: '',
      physical_location: '',
      website: '',
      desired_store_name: '',

      // UI state
      isSubmitting: false,
      submitted: isReturningUser, // Auto-show success screen if returning user
      isReturningUser: isReturningUser, // Track if user came from "Returning user?" link
      requestId: '',
      error: '',

      // Validation errors
      errors: {},

      // Activation token entry
      activationToken: '',
      activating: false,
      activationError: '',

      // Password creation modal
      showPasswordModal: false,
      pendingActivationData: null,  // Stores token data for activation flow

      // Login state (for already activated stores)
      loginStoreId: '',
      loginPassword: '',
      loginError: '',
      loggingIn: false
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.handleActivationTokenChange = this.handleActivationTokenChange.bind(this);
    this.handleActivateWithToken = this.handleActivateWithToken.bind(this);
    this.handleLoginStoreIdChange = this.handleLoginStoreIdChange.bind(this);
    this.handleLoginPasswordChange = this.handleLoginPasswordChange.bind(this);
    this.handleLogin = this.handleLogin.bind(this);
  }

  componentDidMount() {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }

  handleInputChange(event) {
    const { name, value } = event.target;
    this.setState({
      [name]: value,
      errors: { ...this.state.errors, [name]: '' },
      error: ''
    });
  }

  validateForm() {
    const errors = {};
    const { business_name, contact_name, contact_email, desired_store_name } = this.state;

    // Required fields
    if (!business_name.trim()) {
      errors.business_name = 'Business name is required';
    }
    if (!contact_name.trim()) {
      errors.contact_name = 'Contact name is required';
    }
    if (!contact_email.trim()) {
      errors.contact_email = 'Email is required';
    } else {
      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contact_email)) {
        errors.contact_email = 'Please enter a valid email address';
      }
    }
    if (!desired_store_name.trim()) {
      errors.desired_store_name = 'Desired store name is required';
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.validateForm()) {
      return;
    }

    this.setState({ isSubmitting: true, error: '' });

    try {
      const {
        business_name,
        contact_name,
        contact_email,
        contact_phone,
        business_description,
        business_category,
        average_transaction_size,
        physical_location,
        website,
        desired_store_name
      } = this.state;

      const response = await fetch(`${getApiBaseUrl()}/store-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          business_name,
          contact_name,
          contact_email,
          contact_phone,
          business_description,
          business_category,
          average_transaction_size,
          physical_location,
          website,
          desired_store_name
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to submit request');
      }

      const data = await response.json();

      this.setState({
        submitted: true,
        requestId: data.request_id,
        isSubmitting: false
      });

    } catch (error) {
      console.error('Error submitting store request:', error);
      this.setState({
        error: error.message,
        isSubmitting: false
      });
    }
  }

  handleActivationTokenChange(event) {
    this.setState({
      activationToken: event.target.value,
      activationError: ''
    });
  }

  /**
   * Extract token from full URL or use as-is if already a token
   */
  extractToken(input) {
    const trimmed = input.trim();

    // Check if it's a full URL
    if (trimmed.includes('/activate/')) {
      const parts = trimmed.split('/activate/');
      return parts[1] || '';
    }

    // Otherwise assume it's already a token
    return trimmed;
  }

  /**
   * Clear all previous store data from AsyncStorage
   * CRITICAL: Prevents wallet/data mixing between different stores
   */
  async clearPreviousStoreData() {
    const keysToRemove = [
      // Wallet private keys (encrypted)
      'secure_api-key-btc',
      'secure_api-key-ltc',
      'secure_api-key-doge',

      // Legacy unencrypted keys (if they exist)
      'api-key-btc',
      'api-key-ltc',
      'api-key-doge',
      'api-key',

      // Public keys (xpubs)
      'api-xpub-btc',
      'api-xpub-ltc',
      'api-xpub-doge',

      // Payout addresses
      'retailerPayoutAddress',

      // Store settings
      'store-name',
      'posMode',
      'merchantId',
      'tipsMode',
      'discount',
      'splash',
      'fiatCurrency',
      'fiatSymbol',
      'topText',
      'middleText',
      'bottomText'
    ];

    // Remove all old store data
    for (const key of keysToRemove) {
      try {
        await AsyncStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove ${key}:`, error);
      }
    }

    console.log('✅ Cleared previous store data from AsyncStorage');
  }

  async handleActivateWithToken() {
    const { activationToken } = this.state;

    if (!activationToken.trim()) {
      this.setState({ activationError: 'Please enter an activation code' });
      return;
    }

    this.setState({ activating: true, activationError: '' });

    try {
      // Extract token from URL or use as-is
      const token = this.extractToken(activationToken);

      // Step 1: Validate token (with timeout)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      let validateResponse;
      try {
        validateResponse = await fetch(`${getApiBaseUrl()}/activation/validate/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          this.setState({
            activationError: 'Connection timeout. Please check your internet connection and try again.',
            activating: false
          });
          return;
        }
        throw fetchError; // Re-throw other fetch errors
      }

      const validateData = await validateResponse.json();

      if (!validateData.valid) {
        this.setState({
          activationError: validateData.error || 'Invalid activation code',
          activating: false
        });
        return;
      }

      // Step 2: Show password creation modal
      this.setState({
        showPasswordModal: true,
        pendingActivationData: { token, storeName: validateData.storeName },
        activating: false
      });

    } catch (error) {
      console.error('Activation error during validation:', error);
      this.setState({
        activationError: 'Failed to connect to server. Please try again.',
        activating: false
      });
    }
  }

  async handlePasswordSubmit(password) {
    const { pendingActivationData } = this.state;

    if (!pendingActivationData) {
      this.setState({ showPasswordModal: false });
      return;
    }

    // Validate password
    if (password.length < 8) {
      window.alert('Password must be at least 8 characters');
      return;
    }

    if (!/\d/.test(password) || !/[a-zA-Z]/.test(password)) {
      window.alert('Password should contain both letters and numbers');
      return;
    }

    this.setState({ showPasswordModal: false, activating: true });

    try {
      const { token } = pendingActivationData;

      // Complete activation
      const completeResponse = await fetch(`${getApiBaseUrl()}/activation/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const completeData = await completeResponse.json();

      if (completeData.success) {
        // CRITICAL: Clear all previous store data BEFORE saving new store
        // This prevents wallet mixing between different stores
        await this.clearPreviousStoreData();

        // Save credentials to AsyncStorage
        await AsyncStorage.setItem('store-id', completeData.storeId);
        await AsyncStorage.setItem('storePassword', password);

        // Show success message
        window.alert(
          'Activation Complete! 🎉\n\n' +
          'Welcome to Ðijitto Express!\n\n' +
          'Your store is now activated and ready to accept crypto payments.'
        );

        // Navigate to Admin
        window.location.href = '/admin';
      } else {
        this.setState({
          activationError: completeData.error || 'Failed to activate store',
          activating: false
        });
      }

    } catch (error) {
      console.error('Activation error:', error);

      let errorMessage = 'Network error. Please try again.';

      // Check for specific error types
      if (error.message && error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to server. Please check if the Dijitto app is running and try again.';
      } else if (error.name === 'TypeError') {
        errorMessage = 'Connection error. Make sure you\'re using the activation link inside the Dijitto app, not in a web browser.';
      }

      this.setState({
        activationError: errorMessage,
        activating: false
      });
    }
  }

  // Login handlers for already activated stores
  handleLoginStoreIdChange(event) {
    this.setState({ loginStoreId: event.target.value, loginError: '' });
  }

  handleLoginPasswordChange(event) {
    this.setState({ loginPassword: event.target.value, loginError: '' });
  }

  async handleLogin() {
    const { loginStoreId, loginPassword } = this.state;

    if (!loginStoreId.trim() || !loginPassword.trim()) {
      this.setState({ loginError: 'Please enter Store ID and Password' });
      return;
    }

    this.setState({ loggingIn: true, loginError: '' });

    try {
      const API_BASE = getApiBaseUrl();
      const response = await fetch(`${API_BASE}/stores/${loginStoreId}/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: loginPassword })
      });

      const data = await response.json();

      if (data.valid) {
        // Clear any old store data
        await this.clearPreviousStoreData();

        // Save credentials
        await AsyncStorage.setItem('store-id', loginStoreId);
        await AsyncStorage.setItem('storePassword', loginPassword);

        // Navigate to admin
        window.location.href = '/admin';
      } else {
        this.setState({ loginError: 'Invalid Store ID or Password', loggingIn: false });
      }
    } catch (error) {
      console.error('Login error:', error);
      this.setState({ loginError: 'Connection error. Please try again.', loggingIn: false });
    }
  }

  render() {
    const { submitted, requestId, isSubmitting, error, errors, activationToken, activating, activationError } = this.state;

    if (submitted) {
      return (
        <Container fluid className="App">
          <Row className="justify-content-center">
            <Col xs={12} md={8} lg={6}>
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                {/* Only show waitlist messaging for new signups, not returning users */}
                {!this.state.isReturningUser && (
                  <>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎉</div>
                    <h2 style={{ color: '#28a745', marginBottom: '20px' }}>You're on the Waitlist!</h2>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                      Thank you for your interest in joining Ðijitto.
                    </p>
                    <p style={{ marginBottom: '20px' }}>
                      Your waitlist ID: <strong>{requestId}</strong>
                    </p>
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <p><strong>What happens next?</strong></p>
                      <p>You've been added to our merchant waitlist. We're onboarding new merchants as capacity allows.</p>
                      <p>When your spot comes up, you'll receive an email at <strong>{this.state.contact_email}</strong> with your store credentials and next steps.</p>
                      <p style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>We appreciate your patience as we grow the Ðijitto network!</p>
                    </div>
                  </>
                )}

                {/* Activation Token Entry Section */}
                <div style={{
                  border: '2px solid #007bff',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '20px',
                  backgroundColor: '#f0f8ff',
                  textAlign: 'left'
                }}>
                  <h4 style={{ color: '#007bff', marginBottom: '15px', textAlign: 'center' }}>
                    🎉 Already Approved?
                  </h4>
                  <p style={{ fontSize: '14px', marginBottom: '15px', textAlign: 'center' }}>
                    If your request was approved, enter your activation code below to get started immediately:
                  </p>

                  <input
                    type="text"
                    value={activationToken}
                    onChange={this.handleActivationTokenChange}
                    placeholder="Enter activation code or paste full URL"
                    disabled={activating}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      border: activationError ? '2px solid #dc3545' : '1px solid #ced4da',
                      borderRadius: '4px',
                      marginBottom: '10px'
                    }}
                  />

                  {activationError && (
                    <div style={{
                      backgroundColor: '#f8d7da',
                      color: '#721c24',
                      padding: '10px',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      fontSize: '14px'
                    }}>
                      {activationError}
                    </div>
                  )}

                  <button
                    onClick={this.handleActivateWithToken}
                    disabled={activating || !activationToken.trim()}
                    style={{
                      width: '100%',
                      backgroundColor: activating || !activationToken.trim() ? '#6c757d' : '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: activating || !activationToken.trim() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {activating ? 'Activating...' : 'Activate Now'}
                  </button>

                  <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', textAlign: 'center' }}>
                    💡 Tip: You can paste the full activation URL or just the code
                  </p>
                </div>

                {/* Login Section for Already Activated Stores */}
                <div style={{
                  border: '2px solid #6c757d',
                  borderRadius: '8px',
                  padding: '20px',
                  marginTop: '20px',
                  backgroundColor: '#f8f9fa',
                  textAlign: 'left'
                }}>
                  <h4 style={{ color: '#495057', marginBottom: '15px', textAlign: 'center' }}>
                    🔑 Already Activated?
                  </h4>
                  <p style={{ fontSize: '14px', marginBottom: '15px', textAlign: 'center' }}>
                    If you've already activated your store, sign in with your Store ID and password:
                  </p>

                  <input
                    type="text"
                    value={this.state.loginStoreId}
                    onChange={this.handleLoginStoreIdChange}
                    placeholder="Store ID"
                    disabled={this.state.loggingIn}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      marginBottom: '10px'
                    }}
                  />

                  <input
                    type="password"
                    value={this.state.loginPassword}
                    onChange={this.handleLoginPasswordChange}
                    placeholder="Password"
                    disabled={this.state.loggingIn}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      marginBottom: '10px'
                    }}
                  />

                  {this.state.loginError && (
                    <div style={{
                      backgroundColor: '#f8d7da',
                      color: '#721c24',
                      padding: '10px',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      fontSize: '14px'
                    }}>
                      {this.state.loginError}
                    </div>
                  )}

                  <button
                    onClick={this.handleLogin}
                    disabled={this.state.loggingIn || !this.state.loginStoreId.trim() || !this.state.loginPassword.trim()}
                    style={{
                      width: '100%',
                      backgroundColor: this.state.loggingIn ? '#6c757d' : '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: this.state.loggingIn ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {this.state.loggingIn ? 'Signing In...' : 'Sign In'}
                  </button>
                </div>

                <div style={{
                  borderTop: '1px solid #ddd',
                  paddingTop: '20px',
                  marginTop: '20px'
                }}>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '0' }}>
                    ⏱️ Still waiting for approval? We'll email you when your request is reviewed.
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Password Creation Modal */}
          <PasswordPrompt
            show={this.state.showPasswordModal}
            title="Create Store Password"
            message={`Create a secure password for ${this.state.pendingActivationData?.storeName || 'your store'}. This password will encrypt your wallet keys. Minimum 8 characters, include letters and numbers.`}
            onSubmit={(pw) => this.handlePasswordSubmit(pw)}
            onCancel={() => this.setState({ showPasswordModal: false, activating: false, pendingActivationData: null })}
            confirmMode={true}
            submitLabel="Create Password"
          />
        </Container>
      );
    }

    return (
      <Container fluid className="App">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <div style={{ padding: '40px 20px' }}>
              <h1 style={{ textAlign: 'center', marginBottom: '10px' }}>Join the Ðijitto Waitlist</h1>
              <p style={{ textAlign: 'center', color: '#666', marginBottom: '40px' }}>
                We're onboarding new merchants as capacity allows. Fill out the form below to join our waitlist.
              </p>

              {error && (
                <div style={{
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  padding: '15px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  border: '1px solid #f5c6cb'
                }}>
                  <strong>Error:</strong> {error}
                </div>
              )}

              <form onSubmit={this.handleSubmit}>
                {/* Business Information */}
                <h3 style={{ marginBottom: '20px', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                  Business Information
                </h3>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Business Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="business_name"
                    value={this.state.business_name}
                    onChange={this.handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: errors.business_name ? '2px solid #dc3545' : '1px solid #ced4da',
                      borderRadius: '4px'
                    }}
                    placeholder="Enter your business name"
                  />
                  {errors.business_name && (
                    <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px' }}>
                      {errors.business_name}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Desired Store Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="desired_store_name"
                    value={this.state.desired_store_name}
                    onChange={this.handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: errors.desired_store_name ? '2px solid #dc3545' : '1px solid #ced4da',
                      borderRadius: '4px'
                    }}
                    placeholder="What should we call your store?"
                  />
                  {errors.desired_store_name && (
                    <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px' }}>
                      {errors.desired_store_name}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Business Description
                  </label>
                  <textarea
                    name="business_description"
                    value={this.state.business_description}
                    onChange={this.handleInputChange}
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px'
                    }}
                    placeholder="Tell us about your business and what you sell"
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Business Category
                  </label>
                  <select
                    name="business_category"
                    value={this.state.business_category}
                    onChange={this.handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select a category</option>
                    <option value="retail">Retail Store</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="bar">Bar</option>
                    <option value="cafe">Cafe</option>
                    <option value="auto_repair">Auto Repair</option>
                    <option value="hotel">Hotel</option>
                    <option value="professional_services">Professional Services</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Average Transaction Size
                  </label>
                  <select
                    name="average_transaction_size"
                    value={this.state.average_transaction_size}
                    onChange={this.handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select a range</option>
                    <option value="1-10">$1 - $10</option>
                    <option value="11-50">$11 - $50</option>
                    <option value="51-100">$51 - $100</option>
                    <option value="101-500">$101 - $500</option>
                    <option value="500+">$500+</option>
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Physical Location
                  </label>
                  <input
                    type="text"
                    name="physical_location"
                    value={this.state.physical_location}
                    onChange={this.handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px'
                    }}
                    placeholder="City, State/Country"
                  />
                </div>

                <div style={{ marginBottom: '40px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Website
                  </label>
                  <input
                    type="text"
                    name="website"
                    value={this.state.website}
                    onChange={this.handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px'
                    }}
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                {/* Contact Information */}
                <h3 style={{ marginBottom: '20px', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                  Contact Information
                </h3>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Contact Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="contact_name"
                    value={this.state.contact_name}
                    onChange={this.handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: errors.contact_name ? '2px solid #dc3545' : '1px solid #ced4da',
                      borderRadius: '4px'
                    }}
                    placeholder="Your full name"
                  />
                  {errors.contact_name && (
                    <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px' }}>
                      {errors.contact_name}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Email Address <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="contact_email"
                    value={this.state.contact_email}
                    onChange={this.handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: errors.contact_email ? '2px solid #dc3545' : '1px solid #ced4da',
                      borderRadius: '4px'
                    }}
                    placeholder="your@email.com"
                  />
                  {errors.contact_email && (
                    <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px' }}>
                      {errors.contact_email}
                    </div>
                  )}
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                    We'll send your store credentials to this email
                  </div>
                </div>

                <div style={{ marginBottom: '40px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="contact_phone"
                    value={this.state.contact_phone}
                    onChange={this.handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px'
                    }}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div style={{ textAlign: 'center', marginTop: '40px' }}>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      backgroundColor: isSubmitting ? '#6c757d' : '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '15px 50px',
                      borderRadius: '6px',
                      fontSize: '18px',
                      fontWeight: '500',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {isSubmitting ? 'Joining Waitlist...' : 'Join Waitlist'}
                  </button>
                </div>

                <div style={{ textAlign: 'center', marginTop: '20px', color: '#666', fontSize: '14px' }}>
                  <p>* Required fields</p>
                </div>
              </form>

              {/* Activation Token Entry Section - Also shown BEFORE submission for returning users */}
              <div style={{
                border: '2px solid #007bff',
                borderRadius: '8px',
                padding: '20px',
                marginTop: '40px',
                marginBottom: '20px',
                backgroundColor: '#f0f8ff',
                textAlign: 'left'
              }}>
                <h4 style={{ color: '#007bff', marginBottom: '15px', textAlign: 'center' }}>
                  🎉 Already Approved?
                </h4>
                <p style={{ fontSize: '14px', marginBottom: '15px', textAlign: 'center' }}>
                  If your request was approved, enter your activation code below to get started immediately:
                </p>

                <input
                  type="text"
                  value={activationToken}
                  onChange={this.handleActivationTokenChange}
                  placeholder="Enter activation code or paste full URL"
                  disabled={activating}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: activationError ? '2px solid #dc3545' : '1px solid #ced4da',
                    borderRadius: '4px',
                    marginBottom: '10px'
                  }}
                />

                {activationError && (
                  <div style={{
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    padding: '10px',
                    borderRadius: '4px',
                    marginBottom: '10px',
                    fontSize: '14px'
                  }}>
                    {activationError}
                  </div>
                )}

                <button
                  onClick={this.handleActivateWithToken}
                  disabled={activating || !activationToken.trim()}
                  style={{
                    width: '100%',
                    backgroundColor: activating || !activationToken.trim() ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: activating || !activationToken.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  {activating ? 'Activating...' : 'Activate Now'}
                </button>

                <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', textAlign: 'center' }}>
                  💡 Tip: You can paste the full activation URL or just the code
                </p>
              </div>

              {/* Login Section for Already Activated Stores */}
              <div style={{
                border: '2px solid #6c757d',
                borderRadius: '8px',
                padding: '20px',
                marginTop: '20px',
                backgroundColor: '#f8f9fa',
                textAlign: 'left'
              }}>
                <h4 style={{ color: '#495057', marginBottom: '15px', textAlign: 'center' }}>
                  🔑 Already Activated?
                </h4>
                <p style={{ fontSize: '14px', marginBottom: '15px', textAlign: 'center' }}>
                  If you've already activated your store, sign in with your Store ID and password:
                </p>

                <input
                  type="text"
                  value={this.state.loginStoreId}
                  onChange={this.handleLoginStoreIdChange}
                  placeholder="Store ID"
                  disabled={this.state.loggingIn}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    marginBottom: '10px'
                  }}
                />

                <input
                  type="password"
                  value={this.state.loginPassword}
                  onChange={this.handleLoginPasswordChange}
                  placeholder="Password"
                  disabled={this.state.loggingIn}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    marginBottom: '10px'
                  }}
                />

                {this.state.loginError && (
                  <div style={{
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    padding: '10px',
                    borderRadius: '4px',
                    marginBottom: '10px',
                    fontSize: '14px'
                  }}>
                    {this.state.loginError}
                  </div>
                )}

                <button
                  onClick={this.handleLogin}
                  disabled={this.state.loggingIn || !this.state.loginStoreId.trim() || !this.state.loginPassword.trim()}
                  style={{
                    width: '100%',
                    backgroundColor: this.state.loggingIn ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: this.state.loggingIn ? 'not-allowed' : 'pointer'
                  }}
                >
                  {this.state.loggingIn ? 'Signing In...' : 'Sign In'}
                </button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Password Creation Modal */}
        <PasswordPrompt
          show={this.state.showPasswordModal}
          title="Create Store Password"
          message={`Create a secure password for ${this.state.pendingActivationData?.storeName || 'your store'}. This password will encrypt your wallet keys. Minimum 8 characters, include letters and numbers.`}
          onSubmit={(pw) => this.handlePasswordSubmit(pw)}
          onCancel={() => this.setState({ showPasswordModal: false, activating: false, pendingActivationData: null })}
          confirmMode={true}
          submitLabel="Create Password"
        />
      </Container>
    );
  }
}
